import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  EyeIcon, 
  PencilIcon, 
  PhotoIcon,
  TagIcon,
  UserIcon,
  CalendarIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { Editor } from '@tinymce/tinymce-react';

// Load TinyMCE core + theme locally to avoid API key validation
// Import order is important - core first, then themes, then plugins, then skins
import 'tinymce/tinymce';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/icons/default';

// Load plugins locally (only the ones we actually use)
import 'tinymce/plugins/link';
import 'tinymce/plugins/table';
import 'tinymce/plugins/code';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/media';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/codesample';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/wordcount';

// Load skins locally so it looks like cloud version
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';

import { formatDateTime } from '../utils/dateUtils';
import { blogEditorConfig } from '../utils/tinymceConfig';
import { useAuth } from '../contexts/AuthContext';

const BlogModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  document, 
  mode = 'add' // 'add', 'edit', 'view'
}) => {
  const { currentUser, userRole, isAdmin } = useAuth();
  const { register, handleSubmit, reset, setValue, watch, getValues, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [contentPreview, setContentPreview] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [htmlContent, setHtmlContent] = useState('');
  const [editorMode, setEditorMode] = useState('html'); // 'html' or 'markdown'
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);

  const watchedContent = watch('content', '');
  const watchedLink = watch('link', '');

  // Set userId and author when creating new blog
  useEffect(() => {
    if (mode === 'add' && currentUser) {
      setValue('userId', currentUser.uid);
      setValue('author', currentUser.email || currentUser.displayName || currentUser.uid);
    }
  }, [mode, currentUser, setValue]);

  // Image upload handler for TinyMCE
  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject('No file selected');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        reject('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        reject('Image size must be less than 5MB');
        return;
      }

      setUploadingImage(true);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setUploadingImage(false);
        resolve(base64);
      };
      reader.onerror = () => {
        setUploadingImage(false);
        reject('Error reading file');
      };
      reader.readAsDataURL(file);
    });
  };

  // TinyMCE configuration using shared config
  const tinyMCEConfig = blogEditorConfig({
    images_upload_handler: (blobInfo, progress) => new Promise((resolve, reject) => {
      const file = blobInfo.blob();
      handleImageUpload(file)
        .then(resolve)
        .catch(reject);
    }),
    setup: (editor) => {
      try {
        // Store editor reference only if editor is valid
        if (editor && typeof editor === 'object') {
          editorRef.current = editor;
          
          // Add event listeners for content changes
          editor.on('input change paste', () => {
            try {
              if (editor && typeof editor.getContent === 'function') {
                const content = editor.getContent({ format: 'text' });
                const words = content.trim().split(/\s+/).length;
                setWordCount(content.trim() === '' ? 0 : words);
              }
            } catch (error) {
              console.warn('TinyMCE content change error:', error);
            }
          });
          
          // Ensure editor is properly initialized
          editor.on('init', () => {
            try {
              if (editor && typeof editor.setContent === 'function' && htmlContent) {
                editor.setContent(htmlContent);
              }
              setEditorReady(true);
            } catch (error) {
              console.warn('TinyMCE init error:', error);
            }
          });
        }
      } catch (error) {
        console.warn('TinyMCE setup error:', error);
      }
    }
  });

  // Handle TinyMCE editor changes
  const handleEditorChange = (content) => {
    try {
      if (content && typeof content === 'string') {
        setHtmlContent(content);
        // Word count is handled in the setup function
      }
    } catch (error) {
      console.warn('Error handling editor change:', error);
    }
  };

  // Helper function to check if editor is ready
  const isEditorReady = () => {
    try {
      const editor = editorRef.current;
      return editor && 
             typeof editor === 'object' && 
             typeof editor.insertContent === 'function' && 
             editor.initialized === true &&
             editorReady;
    } catch (error) {
      console.warn('Error checking editor readiness:', error);
      return false;
    }
  };

  // Helper function to insert HTML content into TinyMCE editor
  const insertHtmlContent = (htmlString) => {
    try {
      if (isEditorReady() && htmlString && typeof htmlString === 'string') {
        const editor = editorRef.current;
        if (editor && typeof editor.insertContent === 'function') {
          editor.insertContent(htmlString);
        }
      }
    } catch (error) {
      console.warn('Error inserting HTML content:', error);
    }
  };

  // Featured image upload handler
  const handleFeaturedImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject('No file selected');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        reject('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        reject('Image size must be less than 5MB');
        return;
      }

      setUploadingFeaturedImage(true);

      // Convert to base64 for immediate display
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setUploadingFeaturedImage(false);
        resolve(base64);
      };
      reader.onerror = () => {
        setUploadingFeaturedImage(false);
        reject('Error reading file');
      };
      reader.readAsDataURL(file);
    });
  };

  // Update word count based on current content (HTML or markdown)
  useEffect(() => {
    const content = editorMode === 'html' ? htmlContent : watchedContent;
    if (content) {
      // Strip HTML tags for word count
      const textContent = content.replace(/<[^>]*>/g, '');
      const words = textContent.trim().split(/\s+/).length;
      setWordCount(textContent.trim() === '' ? 0 : words);
    } else {
      setWordCount(0);
    }
  }, [htmlContent, watchedContent, editorMode]);

  useEffect(() => {
    if (watchedLink) {
      setImagePreview(watchedLink);
    }
  }, [watchedLink]);

  useEffect(() => {
    if (isOpen && document && mode !== 'add') {
      // Populate form with document data
      Object.keys(document).forEach(key => {
        if (key !== 'id') {
          let value = document[key];
          // Handle Firestore timestamps
          if (value && typeof value === 'object' && value._seconds) {
            if (key === 'timestamp' || key === 'createdAt' || key === 'updatedAt') {
              value = new Date(value._seconds * 1000).toISOString().slice(0, 16);
            } else {
              const date = new Date(value._seconds * 1000);
              value = isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
            }
          }
          // Handle arrays
          if (Array.isArray(value)) {
            value = value.join(', ');
          }
          setValue(key, value);
        }
      });
      
      // Set image preview
      if (document.link) {
        setImagePreview(document.link);
      }

      // Set HTML content
      if (document.content) {
        setHtmlContent(document.content);
      }
    } else if (isOpen && mode === 'add') {
      // Safely reset form and editor state
      try {
        if (reset && typeof reset === 'function') {
          // reset();
        }
        setImagePreview('');
        setHtmlContent('');
        setValue('status', 'draft');
        setValue('timestamp', new Date().toISOString().slice(0, 16));
        
        // Reset editor content safely
        if (editorRef.current && typeof editorRef.current.setContent === 'function') {
          editorRef.current.setContent('');
        }
      } catch (error) {
        console.warn('Error during form reset:', error);
      }
    }
  }, [isOpen, document, mode, setValue, reset]);

  // Handle editor mode switching to prevent typing conflicts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (editorMode === 'html') {
        // When switching to HTML mode, sync markdown content to HTML if needed
        const markdownContent = getValues('content');
        if (markdownContent && !htmlContent) {
          setHtmlContent(markdownContent);
        }
        // Clear the form field to avoid conflicts
        setValue('content', '');
      } else {
        // When switching to markdown mode, sync HTML content to form field
        if (htmlContent) {
          setValue('content', htmlContent);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [editorMode, setValue, getValues, htmlContent]);

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      try {
        const editor = editorRef.current;
        if (editor && typeof editor === 'object' && typeof editor.destroy === 'function') {
          editor.destroy();
          editorRef.current = null; // Clear the reference
        }
      } catch (error) {
        console.warn('Error destroying editor:', error);
        editorRef.current = null; // Clear the reference even on error
      }
    };
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Check permissions for editing
      if (mode === 'edit' && !isAdmin) {
        if (userRole === 'author') {
          const isOwner = document?.userId === currentUser?.uid || 
                         document?.author === currentUser?.uid ||
                         document?.author === currentUser?.email ||
                         document?.author === currentUser?.displayName;
          if (!isOwner) {
            alert('You do not have permission to edit this blog post.');
            setLoading(false);
            return;
          }
        }
      }
      
      const processedData = { ...data };
      
      // Handle content based on editor mode
      if (editorMode === 'html') {
        // Validate HTML content
        if (!htmlContent.trim()) {
          alert('Content is required');
          setLoading(false);
          return;
        }
        processedData.content = htmlContent;
      } else {
        // Markdown content comes from form data
        processedData.content = data.content;
      }
      
      // Process tags
      if (processedData.tags) {
        processedData.tags = processedData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      }
      
      // Process categories
      if (processedData.categories) {
        processedData.categories = processedData.categories.split(',').map(cat => cat.trim()).filter(cat => cat);
      }
      
      // Handle timestamp
      if (processedData.timestamp) {
        processedData.timestamp = {
          _seconds: Math.floor(new Date(processedData.timestamp).getTime() / 1000),
          _nanoseconds: 0
        };
      }
      
      // Add metadata
      if (mode === 'add') {
        processedData.createdAt = {
          _seconds: Math.floor(Date.now() / 1000),
          _nanoseconds: 0
        };
        // Add the current user's ID when creating a new blog
        if (currentUser) {
          processedData.userId = currentUser.uid;
          // Also set author field for backward compatibility
          processedData.author = currentUser.email || currentUser.displayName || currentUser.uid;
        }
      }
      processedData.updatedAt = {
        _seconds: Math.floor(Date.now() / 1000),
        _nanoseconds: 0
      };
      
      // Word count and editor mode
      processedData.wordCount = wordCount;
      processedData.contentType = editorMode; // Store the editor mode used
      
      await onSave(processedData);
      onClose();
    } catch (error) {
      console.error('Error saving blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'text-green-600 bg-green-100';
      case 'draft': return 'text-yellow-600 bg-yellow-100';
      case 'archived': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published': return CheckCircleIcon;
      case 'draft': return ClockIcon;
      case 'archived': return ExclamationTriangleIcon;
      default: return ClockIcon;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-8 w-8" />
                          <div>
                <h2 className="text-2xl font-bold">
                  {mode === 'add' ? 'Create New Blog Post' : mode === 'edit' ? 'Edit Blog Post' : 'View Blog Post'}
                </h2>
                {document && (
                  <p className="text-indigo-100 text-sm">
                    ID: {document.id} • {wordCount} words
                  </p>
                )}
                {!isAdmin && userRole === 'author' && (
                  <p className="text-indigo-100 text-sm">
                    Author Mode • You can only edit your own posts
                  </p>
                )}
              </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-indigo-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(95vh-200px)]">
          {/* Left Panel - Form */}
          <div className="flex-1 p-6 overflow-y-auto">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Basic Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      {...register('title', { required: 'Title is required' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                      placeholder="Enter blog post title..."
                      disabled={mode === 'view'}
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      {...register('description', { required: 'Description is required' })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Brief description or excerpt..."
                      disabled={mode === 'view'}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <UserIcon className="h-4 w-4 inline mr-1" />
                      Author ID
                    </label>
                    <input
                      {...register('userId')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Author user ID..."
                      disabled={mode === 'view' || (mode === 'edit' && !isAdmin)}
                      value={mode === 'add' ? (currentUser?.uid || '') : (document?.userId || '')}
                    />
                    {mode === 'add' && (
                      <p className="text-xs text-gray-500 mt-1">Will be automatically set to your user ID</p>
                    )}
                    {mode === 'edit' && !isAdmin && (
                      <p className="text-xs text-gray-500 mt-1">Only administrators can change the author</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <UserIcon className="h-4 w-4 inline mr-1" />
                      Author Name/Email (Legacy)
                    </label>
                    <input
                      {...register('author')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Author name or email..."
                      disabled={mode === 'view' || (mode === 'edit' && !isAdmin)}
                      value={mode === 'add' ? (currentUser?.email || currentUser?.displayName || '') : (document?.author || '')}
                    />
                    {mode === 'add' && (
                      <p className="text-xs text-gray-500 mt-1">Will be automatically set to your email/name for backward compatibility</p>
                    )}
                    {mode === 'edit' && !isAdmin && (
                      <p className="text-xs text-gray-500 mt-1">Only administrators can change the author</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={mode === 'view'}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <TagIcon className="h-4 w-4 inline mr-1" />
                      Tags
                    </label>
                    <input
                      {...register('tags')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="tag1, tag2, tag3..."
                      disabled={mode === 'view'}
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categories
                    </label>
                    <input
                      {...register('categories')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="category1, category2..."
                      disabled={mode === 'view'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CalendarIcon className="h-4 w-4 inline mr-1" />
                      Publish Date
                    </label>
                    <input
                      {...register('timestamp')}
                      type="datetime-local"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={mode === 'view'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                      SEO Slug
                    </label>
                    <input
                      {...register('slug')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="url-friendly-slug"
                      disabled={mode === 'view'}
                    />
                  </div>
                </div>
              </div>

              {/* Featured Image */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <PhotoIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Featured Image
                </h3>
                
                <div className="space-y-4">
                  {/* Upload Section */}
                  {mode !== 'view' && (
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                        dragOver 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-300 hover:border-indigo-400'
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        setDragOver(false);
                        const files = Array.from(e.dataTransfer.files);
                        const imageFile = files.find(file => file.type.startsWith('image/'));
                        
                        if (imageFile) {
                          try {
                            const imageUrl = await handleFeaturedImageUpload(imageFile);
                            setValue('link', imageUrl);
                            setImagePreview(imageUrl);
                          } catch (error) {
                            alert('Error uploading featured image: ' + error);
                          }
                        }
                      }}
                    >
                      <div className="text-center">
                        <PhotoIcon className={`mx-auto h-12 w-12 ${dragOver ? 'text-indigo-500' : 'text-gray-400'}`} />
                        <div className="mt-3">
                          <label htmlFor="featured-image-upload" className="cursor-pointer">
                            <span className={`mt-2 block text-sm font-medium ${dragOver ? 'text-indigo-900' : 'text-gray-900'}`}>
                              {dragOver ? 'Drop image here' : 'Upload Featured Image'}
                            </span>
                            <span className={`mt-1 block text-sm ${dragOver ? 'text-indigo-700' : 'text-gray-500'}`}>
                              {dragOver ? 'Release to upload' : 'Click to upload or drag and drop'}
                            </span>
                            <span className="mt-1 block text-xs text-gray-400">
                              PNG, JPG, GIF, WebP up to 5MB
                            </span>
                          </label>
                          <input
                            id="featured-image-upload"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  const imageUrl = await handleFeaturedImageUpload(file);
                                  setValue('link', imageUrl);
                                  setImagePreview(imageUrl);
                                } catch (error) {
                                  alert('Error uploading featured image: ' + error);
                                }
                              }
                            }}
                            disabled={uploadingFeaturedImage}
                          />
                        </div>
                        {uploadingFeaturedImage && (
                          <div className="mt-3 flex items-center justify-center text-sm text-indigo-600">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading featured image...
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* URL Input (Alternative) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Or enter Image URL
                    </label>
                    <input
                      {...register('link')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                      disabled={mode === 'view'}
                    />
                  </div>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">Preview:</p>
                        {mode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => {
                              setValue('link', '');
                              setImagePreview('');
                            }}
                            className="text-sm text-red-600 hover:text-red-800 font-medium"
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Featured image preview"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200 shadow-sm"
                          onError={() => setImagePreview('')}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                    Content
                  </h3>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">
                      {wordCount} words
                    </span>
                    
                    {/* Editor Mode Toggle */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => setEditorMode('html')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          editorMode === 'html' 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        disabled={mode === 'view'}
                      >
                        HTML Editor
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode('markdown')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          editorMode === 'markdown' 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        disabled={mode === 'view'}
                      >
                        Markdown
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setContentPreview(!contentPreview)}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      {contentPreview ? 'Edit' : 'Preview'}
                    </button>
                  </div>
                </div>
                
                {!contentPreview ? (
                  <div>
                    {editorMode === 'html' ? (
                      <div>
                        <div className="bg-white rounded-lg border border-gray-300">
                          <div className="relative">
                            <Editor
                              value={htmlContent}
                              onEditorChange={handleEditorChange}
                              init={{
                                ...tinyMCEConfig,
                                readonly: mode === 'view',
                                placeholder: 'Write your blog content here...'
                              }}
                              onInit={() => {
                                // Additional safety check
                                try {
                                  if (editorRef.current && typeof editorRef.current === 'object') {
                                    console.log('TinyMCE editor initialized successfully');
                                  }
                                } catch (error) {
                                  console.warn('TinyMCE onInit error:', error);
                                }
                              }}
                            />
                          </div>
                        </div>

                      </div>
                    ) : (
                      <textarea
                        {...register('content', { required: 'Content is required' })}
                        rows={12}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                        placeholder="Write your blog content here using Markdown..."
                        disabled={mode === 'view'}
                      />
                    )}
                  </div>
                ) : (
                  <div className="w-full min-h-[300px] px-4 py-3 border border-gray-300 rounded-lg bg-white">
                    <div className="prose max-w-none">
                      {editorMode === 'html' ? (
                        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                      ) : watchedContent ? (
                        <pre className="whitespace-pre-wrap font-sans">{watchedContent}</pre>
                      ) : (
                        <p className="text-gray-500 italic">No content to preview</p>
                      )}
                    </div>
                  </div>
                )}
                
                                  {/* Validation errors */}
                  {editorMode === 'html' && !htmlContent && (
                    <p className="text-red-500 text-sm mt-1">Content is required</p>
                  )}
                  {errors.content && editorMode === 'markdown' && (
                    <p className="text-red-500 text-sm mt-1">{errors.content.message}</p>
                  )}
              </div>

              {/* SEO & Meta */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <GlobeAltIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  SEO & Metadata
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description
                    </label>
                    <textarea
                      {...register('metaDescription')}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="SEO meta description (150-160 characters recommended)"
                      disabled={mode === 'view'}
                      maxLength={160}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Keywords
                    </label>
                    <input
                      {...register('metaKeywords')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="keyword1, keyword2, keyword3"
                      disabled={mode === 'view'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reading Time (minutes)
                    </label>
                    <input
                      {...register('readingTime')}
                      type="number"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Estimated reading time"
                      disabled={mode === 'view'}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              {mode !== 'view' && (
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      mode === 'add' ? 'Create Blog Post' : 'Update Blog Post'
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Right Panel - Status & Preview */}
          {mode !== 'add' && document && (
            <div className="w-80 bg-gray-50 border-l border-gray-200 p-6 overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Details</h3>
              
              {/* Status */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(watch('status'))}`}>
                  {React.createElement(getStatusIcon(watch('status')), { className: "h-4 w-4 mr-1" })}
                  {watch('status') || 'draft'}
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-4 text-sm">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {formatDateTime(document.createdAt || document.timestamp, 'Unknown')}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Last Updated
                  </label>
                  <p className="text-gray-900">
                    {formatDateTime(document.updatedAt, 'Never')}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Word Count
                  </label>
                  <p className="text-gray-900">{document.wordCount || wordCount || 0} words</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Author ID
                  </label>
                  <p className="text-gray-900 break-all">{document.userId || 'Not specified'}</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Author Name/Email
                  </label>
                  <p className="text-gray-900 break-all">{document.author || 'Not specified'}</p>
                </div>

                {document.tags && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {(typeof document.tags === 'string' ? document.tags.split(',') : document.tags || []).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogModal;
